/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { MRT_Row } from 'material-react-table';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { Box, IconButton, TableCellProps, Tooltip, Typography } from '@mui/material';
import {
  LuiActionsMenu,
  LuiMaterialTable,
  LuiMaterialTableHeadCellStyles,
  LuiObjectTag
} from '@jda/lui-common-component-library-mui5';
import { useStyles } from './styles';
import { UBER, MACHINE_TYPES, UBER_REALM_CLIENT_TOOLTIP } from './Constants';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { IMachineList } from '../../models/Machines/ClientContracts';
import { permissions } from '../../shared/CheckPermissions/constants';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { selectRealmInfo } from '../../store/realmSettings/realmSettingsSelector';

const MachineList = ({
  loading,
  filteredClients,
  handleListItemClick,
  deleteOnClick,
  extendedToolbar,
  renderEmptyState
}: IMachineList) => {
  const { classes } = useStyles();
  const { height } = useWindowDimensions();

  const selectedRealmInfo = useSelector(selectRealmInfo);

  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const [page, setPage] = useState<any>(1);
  const [pageSize, setPageSize] = useState<any>(50);

  const writeMachinePermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.machine.read_write], assignedPermissions.permissions);

  const columns: any = [
    {
      id: 'uberRealmClient',
      accessorKey: 'isUberRealmClient',
      header: '',
      size: 5,
      muiTableBodyCellProps: {
        sx: {
          width: '20px',
          minWidth: '20px',
          padding: 0,
          textAlign: 'center'
        },
      },
      muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '20px',
          minWidth: '20px',
        })
      } as TableCellProps),
      Cell: ({ row }: { row: any }) => (
        row.original.isUberRealmClient ?
          <Tooltip arrow placement="bottom" title={UBER_REALM_CLIENT_TOOLTIP}>
            <Box>
              <LuiObjectTag
                title={UBER}
                colorVariant="white"
              />
            </Box>
          </Tooltip> : null
      )
    },
    {
      id: 'displayName',
      accessorKey: 'displayName',
      header: 'Machine Name',
      Cell: ({ row }: { row: any }) => (
        <Box className={classes.addSpacing}>
          <Typography variant='body1'>{row.original.displayName}</Typography>
        </Box>
      )
    },
    {
      id: 'subjectId',
      accessorKey: 'subjectId',
      header: 'Subject ID'
    },
    {
      id: 'clientId',
      accessorKey: 'clientId',
      header: 'Client ID',
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: 'Machine Type',
      Cell: ({ row }: { row: any }) => <Typography variant='body1'>{MACHINE_TYPES[row.original.type] || '-'}</Typography>
    },
    {
      id: 'isExternal',
      accessorKey: 'isExternal',
      header: 'Machine Category',
      Cell: ({ row }: { row: any }) => {
        return <Typography variant='body1'>{row.original.type === 'apim' ? (row.original.isExternal ? 'External' : 'Internal') : '-'}</Typography>;
      }
    }
  ];
  if (selectedRealmInfo.envType !== 'admin') {
    columns.splice(5, 1); //removing Machine Category column for non admin env in portalV2
  }
  const customRowActions: any = (row: MRT_Row<any>) => {
    return (
      <LuiActionsMenu
        items={[
          writeMachinePermission
            ? {
              render: (
                <Box data-testid="toolTip-sign">
                  <IconButton
                    aria-label="delete"
                    data-testid="deleteIcon"
                    onClick={() => deleteOnClick(row.original)}
                  >
                    <Trash className={classes.deleteIcon} />
                  </IconButton>
                </Box>
              ),
              alwaysVisible: true
            }
            : []
        ].flat()}
        maxItemsToDisplay={1}
      />
    );
  };

  return (
    <>
      <LuiMaterialTable
        paging={true}
        loading={loading}
        keyAccessor={'id'}
        columns={columns}
        data={filteredClients}
        columnResizable={false}
        enableColumnActions={false}
        isHeaderSticky={true}
        enableAlternateRowStyle={true}
        maxTableBodyHeight={height - 201}
        customRowActions={customRowActions}
        paginationDisplayMode={'extended'}
        headerStyle={'standard'}
        onRowClick={(rowData: any) => handleListItemClick(rowData)}
        paginationState={{
          pageIndex: page - 1 || 0,
          pageSize: pageSize || 50
        }}
        onChangePage={(pageIndex, pageSize) => {
          setPage(pageIndex + 1);
          setPageSize(pageSize);
        }}
        paginationProps={{ rowsPerPageOptions: [50, 100, 200], showFirstButton: true}}
        renderTopToolbar={extendedToolbar}
        renderEmptyRowsFallback={renderEmptyState}
      />
    </>
  );
};

export default MachineList;
